import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Route, BookOpen, Share2, Trash2, Loader2, ArrowLeft, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getBookName } from '../utils/bookTranslations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * StudyPathsListPage — a user's saved study paths, with open / share / delete.
 */
const StudyPathsListPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const es = language === 'es';
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    axios.get(`${BACKEND_URL}/api/study-paths/mine`, { withCredentials: true })
      .then((r) => setPaths(r.data.paths || []))
      .catch(() => setPaths([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sharePath = async (id) => {
    const url = `${BACKEND_URL}/api/study-paths/${id}/share`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Lumera · Study Path', text: 'Follow my Bible study path', url }); return; }
      catch (_) { /* cancelled */ }
    }
    try { await navigator.clipboard.writeText(url); toast.success(es ? '¡Enlace copiado!' : 'Link copied!'); }
    catch (_) { /* blocked */ }
  };

  const deletePath = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/study-paths/${id}`, { withCredentials: true });
      setPaths((prev) => prev.filter((p) => p.id !== id));
      toast.success(es ? 'Ruta eliminada' : 'Path deleted');
    } catch (_) {
      toast.error(es ? 'No se pudo eliminar' : "Couldn't delete");
    }
  };

  const summary = (p) =>
    (p.steps || []).map((s) => `${getBookName(s.book, language)} ${s.chapter}`).join(' › ');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="mb-4" data-testid="study-paths-back">
          <ArrowLeft className="w-4 h-4 mr-1" /> {es ? 'Panel' : 'Dashboard'}
        </Button>

        <div className="flex items-center gap-2 mb-5 text-blue-700 dark:text-blue-300">
          <Route className="w-6 h-6" />
          <h1 className="text-2xl font-bold">{es ? 'Mis Rutas de Estudio' : 'My Study Paths'}</h1>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-7 h-7 animate-spin mx-auto text-blue-500" /></div>
        ) : paths.length === 0 ? (
          <Card data-testid="study-paths-empty">
            <CardContent className="py-12 text-center space-y-3">
              <Route className="w-10 h-10 mx-auto text-blue-300" />
              <p className="text-sm text-muted-foreground">
                {es
                  ? 'Aún no tienes rutas guardadas. En el lector, activa "Significado de nombres", sigue referencias cruzadas y toca "Compartir" en la barra de ruta.'
                  : 'No saved paths yet. In the reader, turn on "Name meanings", follow cross-references, then tap "Share" on the trail bar.'}
              </p>
              <Button onClick={() => navigate('/bible/John/3')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="study-paths-start">
                <BookOpen className="w-4 h-4 mr-1.5" /> {es ? 'Ir al lector' : 'Go to reader'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="study-paths-list">
            {paths.map((p) => (
              <Card key={p.id} className="border-blue-200/60 dark:border-blue-800/40" data-testid={`study-path-item-${p.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="truncate">{p.title}</span>
                    <span className="shrink-0 inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                      <Eye className="w-3.5 h-3.5" /> {p.views}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground">{summary(p)}</p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => navigate(`/path/${p.id}`)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`study-path-open-${p.id}`}>
                      <BookOpen className="w-3.5 h-3.5 mr-1" /> {es ? 'Abrir' : 'Open'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => sharePath(p.id)} data-testid={`study-path-share-${p.id}`}>
                      <Share2 className="w-3.5 h-3.5 mr-1" /> {es ? 'Compartir' : 'Share'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deletePath(p.id)} className="text-destructive hover:bg-destructive/10" data-testid={`study-path-delete-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> {es ? 'Eliminar' : 'Delete'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyPathsListPage;
